import React from 'react';
import DocLayout from '../DocLayout';
import { Target, Plus, Edit } from 'lucide-react';

const CreateEdit = () => {
  return (
    <DocLayout title="Creating and Editing Leads" category="Lead Management">
      <div className="space-y-6">
        <section>
          <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
            <Target className="w-6 h-6 text-indigo-600" />
            Lead Management Basics
          </h2>
          <p className="text-slate-700 mb-4">
            Leads are the foundation of your sales pipeline in I-Match. Learn how to create, edit, and manage them effectively.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Creating a New Lead</h3>
          <p className="text-slate-700 mb-3">
            You can create leads in two ways:
          </p>
          
          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Plus className="w-5 h-5 text-green-600" />
                Method 1: Manual Entry
              </h4>
              <ol className="list-decimal pl-6 space-y-2 text-slate-700 text-sm">
                <li>Navigate to the Leads page</li>
                <li>Click the "+ New Lead" button</li>
                <li>Fill in the lead details:
                  <ul className="list-disc pl-6 mt-1 space-y-1">
                    <li><strong>Name</strong>: Contact's full name</li>
                    <li><strong>Email</strong>: Primary email address</li>
                    <li><strong>Phone</strong>: Contact number</li>
                    <li><strong>Company</strong>: Organization name</li>
                    <li><strong>Title</strong>: Job position</li>
                    <li><strong>Website</strong>: Company website</li>
                    <li><strong>Source</strong>: How you found this lead</li>
                    <li><strong>Stage</strong>: Current pipeline stage</li>
                    <li><strong>Owner</strong>: Team member responsible</li>
                  </ul>
                </li>
                <li>Click "Save" to create the lead</li>
              </ol>
            </div>

            <div className="border rounded-lg p-4">
              <h4 className="font-semibold text-slate-900 mb-2 flex items-center gap-2">
                <Plus className="w-5 h-5 text-blue-600" />
                Method 2: CSV Import
              </h4>
              <ol className="list-decimal pl-6 space-y-2 text-slate-700 text-sm">
                <li>Navigate to Import page</li>
                <li>Download the CSV template</li>
                <li>Fill in your lead data following the template format</li>
                <li>Upload the CSV file</li>
                <li>Review import results</li>
                <li>Leads are automatically created and can be enriched</li>
              </ol>
              <div className="bg-blue-50 border border-blue-200 rounded p-3 mt-3">
                <p className="text-sm text-slate-700">
                  <strong>CSV Columns:</strong> name, email, phone, company, title, website, lead_notes
                </p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Editing Lead Information</h3>
          <p className="text-slate-700 mb-3">
            To edit an existing lead:
          </p>
          <ol className="list-decimal pl-6 space-y-2 text-slate-700">
            <li>Click on the lead's name in the leads table</li>
            <li>You'll be taken to the detailed lead page</li>
            <li>Click the <Edit className="w-4 h-4 inline" /> edit icon next to any field</li>
            <li>Update the information</li>
            <li>Changes are auto-saved</li>
          </ol>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Lead Fields Explained</h3>
          <div className="space-y-2">
            <div className="border-l-4 border-indigo-500 pl-4 py-2">
              <h4 className="font-semibold text-slate-900 text-sm">Basic Information</h4>
              <p className="text-slate-600 text-xs">Name, email, phone - essential contact details</p>
            </div>
            <div className="border-l-4 border-blue-500 pl-4 py-2">
              <h4 className="font-semibold text-slate-900 text-sm">Company Details</h4>
              <p className="text-slate-600 text-xs">Company name, title, website - business context</p>
            </div>
            <div className="border-l-4 border-green-500 pl-4 py-2">
              <h4 className="font-semibold text-slate-900 text-sm">Pipeline Information</h4>
              <p className="text-slate-600 text-xs">Stage, source, owner - tracking and assignment</p>
            </div>
            <div className="border-l-4 border-purple-500 pl-4 py-2">
              <h4 className="font-semibold text-slate-900 text-sm">AI Enriched Data</h4>
              <p className="text-slate-600 text-xs">Industry, revenue, employee count - auto-populated by AI</p>
            </div>
          </div>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Managing Lead Status</h3>
          <p className="text-slate-700 mb-3">
            Move leads through your pipeline by updating their stage:
          </p>
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-3 py-1 bg-gray-100 rounded-full text-sm">New</span>
            <span className="text-slate-400">→</span>
            <span className="px-3 py-1 bg-blue-100 rounded-full text-sm">Contacted</span>
            <span className="text-slate-400">→</span>
            <span className="px-3 py-1 bg-indigo-100 rounded-full text-sm">Qualified</span>
            <span className="text-slate-400">→</span>
            <span className="px-3 py-1 bg-purple-100 rounded-full text-sm">Proposal</span>
            <span className="text-slate-400">→</span>
            <span className="px-3 py-1 bg-green-100 rounded-full text-sm">Won</span>
          </div>
          <p className="text-slate-600 text-sm">
            Click on the stage dropdown in the lead details page to change the status.
          </p>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Assigning Lead Owners</h3>
          <p className="text-slate-700 mb-3">
            Assign leads to team members for better accountability:
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700">
            <li>Click the "Assign owner" dropdown in lead details</li>
            <li>Select a team member from the list</li>
            <li>The assigned owner will see the lead in their filtered view</li>
            <li>Use filters to view leads by owner</li>
          </ul>
        </section>

        <section>
          <h3 className="text-xl font-semibold text-slate-900 mb-3">Deleting Leads</h3>
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 mb-2">⚠️ Caution: Deleting Leads</h4>
            <p className="text-slate-700 text-sm mb-2">
              Deleted leads cannot be recovered. Before deleting:
            </p>
            <ul className="list-disc pl-6 space-y-1 text-slate-700 text-sm">
              <li>Consider changing the stage to "Lost" instead</li>
              <li>Export the lead data if you might need it later</li>
              <li>Check for linked contacts, tasks, or notes</li>
            </ul>
          </div>
        </section>

        <section>
          <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
            <h4 className="font-semibold text-slate-900 mb-2">💡 Best Practices</h4>
            <ul className="space-y-1 text-slate-700 text-sm">
              <li>• Always include at least name and email for every lead</li>
              <li>• Use consistent naming conventions for companies</li>
              <li>• Add notes immediately after interactions</li>
              <li>• Keep lead stages updated in real-time</li>
              <li>• Use AI enrichment to fill in missing data</li>
            </ul>
          </div>
        </section>
      </div>
    </DocLayout>
  );
};

export default CreateEdit;